/**
 * Phase 6B — Admin V5 Learning Centre Management Panel.
 *
 * Tabs: Analytics · Topics · Mocks · Questions · Rooms · AI Feedback Logs · Papers.
 * - Create/edit/publish/unpublish topics
 * - Add/delete questions
 * - Create/edit mocks
 * - View student progress (table)
 * - Discussion moderation (admin view)
 * - AI feedback call logs
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, BookOpen, FileText, Trophy, Users, MessageSquare, Sparkles, Upload, BarChart3, Plus, Eye, Power, Image as ImageIcon, X } from 'lucide-react';
import QuestionVisual from '../shared/QuestionVisual';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminLearningCentrePanel = () => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="admin-learning-centre-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><BookOpen className="h-5 w-5 text-red-700"/> Learning Centre Management</h2>
          <p className="text-xs text-gray-500 mt-0.5">Victorian Selective Entry · Topics, Mocks, Discussion Moderation, AI Logs</p>
        </div>
      </div>
      <Tabs defaultValue="analytics" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-8 max-w-5xl mb-4">
          <TabsTrigger value="analytics" data-testid="lc-tab-analytics"><BarChart3 className="h-3.5 w-3.5 mr-1"/>Analytics</TabsTrigger>
          <TabsTrigger value="topics" data-testid="lc-tab-topics"><BookOpen className="h-3.5 w-3.5 mr-1"/>Topics</TabsTrigger>
          <TabsTrigger value="questions" data-testid="lc-tab-questions"><FileText className="h-3.5 w-3.5 mr-1"/>Questions</TabsTrigger>
          <TabsTrigger value="mocks" data-testid="lc-tab-mocks"><Trophy className="h-3.5 w-3.5 mr-1"/>Mocks</TabsTrigger>
          <TabsTrigger value="resources" data-testid="lc-tab-resources"><FileText className="h-3.5 w-3.5 mr-1"/>Resources</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="lc-tab-rooms"><MessageSquare className="h-3.5 w-3.5 mr-1"/>Rooms</TabsTrigger>
          <TabsTrigger value="ai-logs" data-testid="lc-tab-ai-logs"><Sparkles className="h-3.5 w-3.5 mr-1"/>AI Logs</TabsTrigger>
          <TabsTrigger value="papers" data-testid="lc-tab-papers"><Upload className="h-3.5 w-3.5 mr-1"/>Papers</TabsTrigger>
        </TabsList>
        <TabsContent value="analytics"><AnalyticsTab/></TabsContent>
        <TabsContent value="topics"><TopicsTab/></TabsContent>
        <TabsContent value="questions"><QuestionsTab/></TabsContent>
        <TabsContent value="mocks"><MocksTab/></TabsContent>
        <TabsContent value="resources"><ResourcesTab/></TabsContent>
        <TabsContent value="rooms"><RoomsTab/></TabsContent>
        <TabsContent value="ai-logs"><AiLogsTab/></TabsContent>
        <TabsContent value="papers"><PapersTab/></TabsContent>
      </Tabs>
    </div>
  );
};

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    ax.get('/api/admin/learning/analytics')
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load analytics'))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-red-700"/></div>;
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
        <Kpi label="Active learners (7d)" value={data.active_learners_7d}/>
        <Kpi label="Topics completed today" value={data.topics_completed_today}/>
        <Kpi label="Quiz attempts today" value={data.quiz_attempts_today}/>
        <Kpi label="Mock attempts today" value={data.mock_attempts_today}/>
        <Kpi label="Avg score" value={`${data.average_score}%`}/>
        <Kpi label="Completion rate" value={`${data.completion_rate_pct}%`}/>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <ListPanel title="Hardest topics" rows={data.hardest_topics} render={r => `${r.slug} · ${r.avg_score}%`}/>
        <ListPanel title="Least attempted" rows={data.least_attempted} render={r => `${r.title} · ${r.attempt_count} attempts`}/>
        <ListPanel title="Most active tutors" rows={data.most_active_tutors} render={r => `${r.name || r.email} · ${r.post_count} posts`}/>
        <ListPanel title="Most active students" rows={data.most_active_students} render={r => `${r.email} · ${r.attempt_count} attempts`}/>
        <ListPanel title="Global weak topics" rows={data.weakest_topics_global} render={r => `${r.slug} · ${r.avg_score}%`}/>
      </div>
    </div>
  );
};

const Kpi = ({ label, value }) => (
  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-3" data-testid={`lc-kpi-${label.replace(/\s+/g,'-').toLowerCase()}`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-xl font-bold text-gray-900 mt-0.5">{value}</div>
  </div>
);
const ListPanel = ({ title, rows, render }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-xs font-bold text-gray-900 mb-2">{title}</div>
    {(!rows || rows.length === 0) ? <div className="text-[11px] italic text-gray-400">No data</div> :
      <ol className="text-xs text-gray-700 space-y-1 list-decimal pl-5">
        {rows.map((r, i) => <li key={i}>{render(r)}</li>)}
      </ol>}
  </div>
);

const TopicsTab = () => {
  const [rows, setRows] = useState({ mathematics: [], english: [] });
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [m, e] = await Promise.all([
        ax.get('/api/learning/tracks/mathematics/topics'),
        ax.get('/api/learning/tracks/english/topics'),
      ]);
      setRows({ mathematics: m.data?.items || [], english: e.data?.items || [] });
    } catch (err) { toast.error('Failed to load topics'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const publish = async (slug, on) => {
    try {
      await ax.post(`/api/admin/learning/topics/${slug}/${on?'publish':'unpublish'}`);
      toast.success(on?'Published':'Unpublished');
      load();
    } catch (e) { toast.error('Action failed'); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-600">{(rows.mathematics.length + rows.english.length)} topics across both tracks</div>
        <button onClick={()=>setShowCreate(true)} className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="lc-topic-create"><Plus className="h-3 w-3"/> New Topic</button>
      </div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      {['mathematics','english'].map(tr => (
        <div key={tr} className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="text-xs font-bold text-gray-900 capitalize mb-2">{tr} ({rows[tr].length})</div>
          <table className="w-full text-xs">
            <thead><tr className="text-gray-500 text-left text-[10px] uppercase tracking-wider">
              <th className="py-1.5">Title</th><th>Difficulty</th><th>Status</th><th>Est. min</th><th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows[tr].map(t => (
                <tr key={t.slug} className="border-t border-gray-100" data-testid={`lc-topic-row-${t.slug}`}>
                  <td className="py-1.5"><strong>{t.title}</strong><div className="text-gray-400 text-[10px]">{t.slug}</div></td>
                  <td><span className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300">{t.difficulty || '-'}</span></td>
                  <td><span className={`text-[10px] font-bold ${t.status==='published'?'text-emerald-700':'text-amber-700'}`}>{t.status}</span></td>
                  <td>{t.estimated_minutes || '-'}</td>
                  <td className="text-right">
                    <button onClick={()=>publish(t.slug, t.status!=='published')} className="text-[10px] text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid={`lc-publish-${t.slug}`}>
                      <Power className="h-2.5 w-2.5"/> {t.status==='published'?'Unpublish':'Publish'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {showCreate && <CreateTopicModal onClose={()=>setShowCreate(false)} onDone={()=>{ setShowCreate(false); load(); }}/>}
    </div>
  );
};

const CreateTopicModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ slug: '', track_slug: 'mathematics', title: '', summary: '', lesson_notes: '', difficulty: 'Medium', estimated_minutes: 25, mastery_threshold: 80 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.slug || !form.title) { toast.error('Slug and title are required'); return; }
    setBusy(true);
    try {
      await ax.post('/api/admin/learning/topics', form);
      toast.success('Topic created');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="lc-create-topic-modal">
        <div className="text-lg font-bold mb-3">Create Topic</div>
        <div className="space-y-2 text-sm">
          <input placeholder="slug (e.g. ratios-advanced)" value={form.slug} onChange={e=>setForm({...form, slug:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="lc-create-slug"/>
          <select value={form.track_slug} onChange={e=>setForm({...form, track_slug:e.target.value})} className="w-full border rounded px-2 py-1.5">
            <option value="mathematics">Mathematics</option><option value="english">English</option>
          </select>
          <input placeholder="Title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="lc-create-title"/>
          <input placeholder="Summary" value={form.summary} onChange={e=>setForm({...form, summary:e.target.value})} className="w-full border rounded px-2 py-1.5"/>
          <textarea rows={4} placeholder="Lesson notes" value={form.lesson_notes} onChange={e=>setForm({...form, lesson_notes:e.target.value})} className="w-full border rounded px-2 py-1.5"/>
          <div className="grid grid-cols-3 gap-2">
            <select value={form.difficulty} onChange={e=>setForm({...form, difficulty:e.target.value})} className="border rounded px-2 py-1.5"><option>Easy</option><option>Medium</option><option>Hard</option></select>
            <input type="number" min={5} value={form.estimated_minutes} onChange={e=>setForm({...form, estimated_minutes:parseInt(e.target.value)||25})} className="border rounded px-2 py-1.5" placeholder="Minutes"/>
            <input type="number" min={50} max={100} value={form.mastery_threshold} onChange={e=>setForm({...form, mastery_threshold:parseInt(e.target.value)||80})} className="border rounded px-2 py-1.5" placeholder="Mastery %"/>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy} className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider disabled:opacity-40" data-testid="lc-create-submit">{busy?'Saving…':'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const VISUAL_TYPES = ['none', 'table', 'bar_chart', 'line_graph', 'pie_chart', 'coordinate_grid', 'geometry_diagram', 'map', 'pattern_grid', 'number_grid', 'image'];

const VISUAL_SAMPLES = {
  table: { headers: ['A', 'B'], rows: [[1, 2], [3, 4]] },
  bar_chart: { x_title: 'X', y_title: 'Y', labels: ['a', 'b', 'c'], values: [3, 5, 2] },
  line_graph: { x_title: 'Day', y_title: 'Val', x_labels: ['M', 'T', 'W', 'T', 'F'], series: [{ name: 'v', y: [1, 3, 5, 4, 6] }] },
  pie_chart: { slices: [{ label: 'A', value: 60 }, { label: 'B', value: 40 }] },
  coordinate_grid: { x_min: 0, x_max: 6, y_min: 0, y_max: 6, points: [{ x: 2, y: 3, label: 'P' }] },
  geometry_diagram: { shape: 'triangle', vertices: [[10, 90], [90, 90], [50, 10]] },
  map: { nodes: [{ x: 30, y: 40, label: 'A' }, { x: 70, y: 60, label: 'B' }], roads: [[[30, 40], [70, 60]]], north: true },
  pattern_grid: { columns: 3, cells: [{ shape: 'circle', color: '#DC2626' }, { shape: 'square', color: '#111827' }, { shape: '?', color: '#9CA3AF' }] },
  number_grid: { rows: [[1, 2, 3], [4, '?', 6]], highlight: [[1, 1]] },
  image: { image_url: '' },
};

const VisualEditor = ({ question, onClose, onSaved }) => {
  const [type, setType] = useState(question?.visual_type || 'none');
  const [payloadText, setPayloadText] = useState(JSON.stringify(question?.visual_payload || {}, null, 2));
  const [caption, setCaption] = useState(question?.visual_caption || '');
  const [alt, setAlt] = useState(question?.visual_alt_text || '');
  const [saving, setSaving] = useState(false);

  const preview = React.useMemo(() => {
    try { return JSON.parse(payloadText || '{}'); } catch (_e) { return null; }
  }, [payloadText]);

  const loadSample = () => {
    setPayloadText(JSON.stringify(VISUAL_SAMPLES[type] || {}, null, 2));
  };

  const save = async () => {
    let payload;
    try { payload = payloadText ? JSON.parse(payloadText) : {}; }
    catch (_e) { toast.error('visual_payload is not valid JSON'); return; }
    setSaving(true);
    try {
      await ax.patch(`/api/admin/learning/questions/${question.id}/visual`, {
        visual_type: type,
        visual_payload: payload,
        visual_caption: caption,
        visual_alt_text: alt,
      });
      toast.success(type === 'none' ? 'Visual cleared' : `Visual (${type}) saved`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="lc-visual-editor">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Question visual editor</div>
            <div className="font-bold text-sm">{question?.prompt?.slice(0, 100)}…</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="lc-visual-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 grid md:grid-cols-2 gap-4">
          {/* Editor */}
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Visual type</div>
              <select value={type} onChange={e => setType(e.target.value)} data-testid="lc-visual-type"
                className="w-full text-sm border rounded-lg px-3 py-2">
                {VISUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={loadSample} className="mt-2 text-[11px] font-bold text-red-700 hover:underline" data-testid="lc-visual-load-sample">
                Load sample payload
              </button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Caption</div>
              <input value={caption} onChange={e => setCaption(e.target.value)} data-testid="lc-visual-caption"
                className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Short instruction shown above the visual" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Alt text (accessibility)</div>
              <input value={alt} onChange={e => setAlt(e.target.value)} data-testid="lc-visual-alt"
                className="w-full text-sm border rounded-lg px-3 py-2" placeholder="Describe the visual for screen readers" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Payload (JSON)</div>
              <textarea value={payloadText} onChange={e => setPayloadText(e.target.value)}
                data-testid="lc-visual-payload"
                rows={16} className="w-full text-xs border rounded-lg px-3 py-2 font-mono" />
              {preview === null && <div className="text-[10px] text-rose-600 mt-1">JSON invalid</div>}
            </div>
            {type === 'image' && (
              <div className="text-[11px] text-gray-500 italic">For image type, paste a direct URL into <code>image_url</code>.</div>
            )}
            <div className="flex gap-2">
              <button onClick={save} disabled={saving || preview === null}
                data-testid="lc-visual-save"
                className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />} Save visual
              </button>
              <button onClick={onClose}
                className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold uppercase tracking-wider">
                Cancel
              </button>
            </div>
          </div>
          {/* Preview */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Live preview</div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-3" data-testid="lc-visual-preview">
              {type === 'none' && <div className="text-xs text-gray-400 italic">No visual will be shown.</div>}
              {type !== 'none' && preview !== null && (
                <QuestionVisual visual_type={type} visual_payload={preview}
                  visual_caption={caption} visual_alt_text={alt} />
              )}
              {type !== 'none' && preview === null && (
                <div className="text-xs text-rose-600 italic">Fix the JSON to see the preview.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [topicFilter, setTopicFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await ax.get(`/api/admin/learning/questions${topicFilter?`?topic_slug=${topicFilter}`:''}`);
      setRows(r.data?.items || []);
    } catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  }, [topicFilter]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await ax.delete(`/api/admin/learning/questions/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };
  const seedVisuals = async () => {
    if (!window.confirm('Run the visual seed pass (idempotent)?')) return;
    try {
      const r = await ax.post('/api/admin/learning/questions/seed-visuals');
      toast.success(`Updated ${r.data.updated_count} question${r.data.updated_count === 1 ? '' : 's'}`);
      load();
    } catch (_e) { toast.error('Seed failed'); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input placeholder="Filter by topic_slug" value={topicFilter} onChange={e=>setTopicFilter(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="lc-q-filter"/>
        <div className="text-xs text-gray-500">{rows.length} questions</div>
        <button onClick={seedVisuals}
          data-testid="lc-seed-visuals"
          className="ml-auto text-[11px] px-3 py-1.5 rounded-full bg-black text-[#D3D86F] font-bold hover:bg-gray-900 inline-flex items-center gap-1">
          <ImageIcon className="h-3 w-3" /> Seed visuals
        </button>
      </div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {rows.map(q => (
          <div key={q.id} className="px-3 py-2 text-xs" data-testid={`lc-q-row-${q.id}`}>
            <div className="font-bold text-gray-900 flex items-center gap-2">
              <span>{q.topic_slug} · {q.kind}</span>
              {q.visual_type && q.visual_type !== 'none' && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid={`lc-q-visual-badge-${q.id}`}>
                  <ImageIcon className="h-2.5 w-2.5" /> {q.visual_type}
                </span>
              )}
            </div>
            <div className="text-gray-700 line-clamp-2">{q.prompt}</div>
            {q.kind === 'mcq' && q.choices && <div className="text-[10px] text-gray-500 mt-1">{q.choices.length} choices · answer index {q.answer}</div>}
            <div className="flex items-center gap-2 mt-1">
              <button onClick={()=>setEditing(q)} data-testid={`lc-q-visual-${q.id}`}
                className="text-[10px] font-bold text-red-700 hover:underline">
                {q.visual_type && q.visual_type !== 'none' ? 'Edit visual' : 'Set visual'}
              </button>
              {q.visual_type && q.visual_type !== 'none' && (
                <button onClick={()=>setPreviewOpen(q)} data-testid={`lc-q-preview-${q.id}`}
                  className="text-[10px] font-bold text-black hover:underline">
                  Preview
                </button>
              )}
              <button onClick={()=>del(q.id)} className="text-[10px] text-rose-600 hover:underline">Delete</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && !busy && <div className="px-3 py-4 text-xs italic text-gray-400">No questions match.</div>}
      </div>
      {editing && <VisualEditor question={editing} onClose={()=>setEditing(null)} onSaved={load} />}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setPreviewOpen(null)} data-testid="lc-visual-preview-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Student preview</div>
              <button onClick={()=>setPreviewOpen(null)} className="p-2 hover:bg-gray-100 rounded-full"><X className="h-4 w-4" /></button>
            </div>
            <QuestionVisual visual_type={previewOpen.visual_type} visual_payload={previewOpen.visual_payload}
              visual_caption={previewOpen.visual_caption} visual_alt_text={previewOpen.visual_alt_text} />
            <div className="mt-3 text-sm font-medium">{previewOpen.prompt}</div>
            {previewOpen.choices && (
              <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                {previewOpen.choices.map((c, ci) => (
                  <div key={ci} className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5">{String.fromCharCode(65+ci)}. {c}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MocksTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await ax.get('/api/learning/mocks'); setRows(r.data?.items || []); }
    catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500">{rows.length} mocks</div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {rows.map(m => (
          <div key={m.slug} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`lc-mock-${m.slug}`}>
            <div>
              <div className="font-bold text-gray-900">{m.title}</div>
              <div className="text-[10px] text-gray-500">{m.track} · {m.duration_minutes} min · {m.question_count} q · {m.active ? 'Active' : 'Hidden'}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RoomsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    ax.get('/api/admin/learning/rooms')
      .then(r => setRows(r.data?.items || []))
      .catch(() => toast.error('Failed'))
      .finally(() => setBusy(false));
  }, []);
  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">{rows.length} discussion rooms</div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {rows.map(r => (
          <div key={r.room_id} className="px-3 py-2 text-xs" data-testid={`lc-room-${r.room_id}`}>
            <div className="font-bold text-gray-900">{r.title || r.student_email}</div>
            <div className="text-[10px] text-gray-500">{r.student_email} · {r.message_count} messages · {(r.tutor_emails||[]).length} tutors</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AiLogsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    ax.get('/api/admin/learning/ai-feedback-logs?limit=50')
      .then(r => setRows(r.data?.items || []))
      .catch(() => toast.error('Failed'))
      .finally(() => setBusy(false));
  }, []);
  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">{rows.length} recent AI calls</div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {rows.map(r => (
          <div key={r.id} className="px-3 py-2 text-xs" data-testid={`lc-ai-log-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-bold text-gray-900">{r.reference || '(no ref)'} · <span className={r.status==='ok'?'text-emerald-700':'text-rose-700'}>{r.status}</span></div>
              <div className="text-[10px] text-gray-500">{new Date(r.started_at).toLocaleString()}</div>
            </div>
            <div className="text-[10px] text-gray-500">{r.student_email || '(anonymous)'} · {r.model}</div>
            {r.response_text && <div className="text-gray-700 line-clamp-2 mt-1">{r.response_text}</div>}
            {r.error && <div className="text-rose-700 text-[11px] mt-1">Error: {r.error}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const PapersTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await ax.get('/api/admin/learning/papers'); setRows(r.data?.items || []); }
    catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await ax.post('/api/admin/learning/papers/import', fd);
      toast.success('Uploaded');
      load();
    } catch (e) { toast.error('Upload failed'); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input type="file" accept="application/pdf" onChange={e => upload(e.target.files?.[0])} className="text-xs" data-testid="lc-paper-upload"/>
        <div className="text-xs text-gray-500">{rows.length} uploaded</div>
      </div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {rows.map(r => (
          <div key={r.id} className="px-3 py-2 text-xs">
            <div className="font-bold text-gray-900">{r.filename}</div>
            <div className="text-[10px] text-gray-500">{Math.round(r.size/1024)} KB · {r.status} · {new Date(r.uploaded_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ResourcesTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await ax.get('/api/admin/learning/resources'); setRows(r.data?.items || []); }
    catch (e) { toast.error('Failed'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (slug) => {
    if (!window.confirm('Hide this resource?')) return;
    try { await ax.delete(`/api/admin/learning/resources/${slug}`); toast.success('Hidden'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-500">{rows.length} resources</div>
        <button onClick={()=>setShowCreate(true)} className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="lc-resource-create"><Plus className="h-3 w-3"/> New Resource</button>
      </div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {rows.map(r => (
          <div key={r.slug} className="px-3 py-2 text-xs" data-testid={`lc-resource-row-${r.slug}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-900">{r.title}</div>
                <div className="text-[10px] text-gray-500">{r.category} · {r.track} · {r.downloads || 0} opens · {r.active ? 'Active' : 'Hidden'}</div>
              </div>
              {r.active && <button onClick={()=>remove(r.slug)} className="text-[10px] text-rose-700 hover:underline">Hide</button>}
            </div>
            {r.summary && <div className="text-gray-700 mt-1 line-clamp-2">{r.summary}</div>}
          </div>
        ))}
      </div>
      {showCreate && <CreateResourceModal onClose={()=>setShowCreate(false)} onDone={()=>{setShowCreate(false); load();}}/>}
    </div>
  );
};

const CreateResourceModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ slug: '', title: '', category: 'revision-notes', track: 'combined', summary: '', content_text: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.slug || !form.title) { toast.error('Slug and title required'); return; }
    setBusy(true);
    try { await ax.post('/api/admin/learning/resources', form); toast.success('Saved'); onDone(); }
    catch (e) { toast.error('Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="lc-create-resource-modal">
        <div className="text-lg font-bold mb-3">Create / Update Resource</div>
        <div className="space-y-2 text-sm">
          <input placeholder="slug" value={form.slug} onChange={e=>setForm({...form,slug:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="lc-res-slug"/>
          <input placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} className="w-full border rounded px-2 py-1.5"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className="border rounded px-2 py-1.5">
              <option value="formula-sheets">Formula sheets</option><option value="vocabulary-lists">Vocabulary lists</option>
              <option value="grammar-guides">Grammar guides</option><option value="worked-examples">Worked examples</option>
              <option value="revision-notes">Revision notes</option><option value="worksheets">Worksheets</option>
              <option value="homework">Homework</option><option value="challenge">Challenge</option>
            </select>
            <select value={form.track} onChange={e=>setForm({...form,track:e.target.value})} className="border rounded px-2 py-1.5">
              <option value="mathematics">Mathematics</option><option value="english">English</option><option value="combined">Combined</option>
            </select>
          </div>
          <input placeholder="Summary" value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})} className="w-full border rounded px-2 py-1.5"/>
          <textarea rows={6} placeholder="Content (paste notes, formulae or instructions)" value={form.content_text} onChange={e=>setForm({...form,content_text:e.target.value})} className="w-full border rounded px-2 py-1.5"/>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy} className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider disabled:opacity-40" data-testid="lc-res-submit">{busy?'Saving…':'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminLearningCentrePanel;
